import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Image, StyleProp, ViewStyle, Pressable, Text, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Asset } from 'expo-asset';
import * as FileSystem from 'expo-file-system/legacy';
import Share from 'react-native-share';
import ThemedText from '../common/ThemedText';
import { useTheme } from '../../theme/ThemeContext';
import { getTimeAgo } from '../../utils/timeAgo';

type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

export interface PollOption {
  id: string;
  text: string;
  votes: number;
}

export interface PollPostData {
  id: string;
  userId: string;
  user: string;
  location: string;
  avatar?: string;
  caption?: string;
  pollOptions: PollOption[];
  totalVotes: number;
  likes: string;
  comments: string;
  userVote?: string;
  isLiked?: boolean;
  createdAt?: string;
}

const engagementActions: { icon: IoniconName; key: 'likes' | 'comments'; tint?: string }[] = [
  { icon: 'trending-up-outline', key: 'likes' },
  { icon: 'chatbubble-ellipses-outline', key: 'comments' },
];

interface PollPostProps {
  post: PollPostData;
  style?: StyleProp<ViewStyle>;
  currentUserId?: string;
  isAdmin?: boolean;
  onVote?: (optionId: string) => void;
  onLike?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onUserPress?: (userId: string) => void;
  onCommentPress?: () => void;
}

const PollPost: React.FC<PollPostProps> = ({ post, style, currentUserId, isAdmin = false, onVote, onLike, onDelete, onEdit, onUserPress, onCommentPress }) => {
  const { theme } = useTheme();
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    console.log(`[PollPost] mounted, post=${post.id}`);
  }, [post.id]);

  const handleExpandCaption = () => {
    console.log(`[PollPost] caption expanded for post=${post.id}`);
    setShowFullCaption(true);
  };

  const handleVote = (optionId: string) => {
    console.log(`[PollPost] voted for option=${optionId} in post=${post.id}`);
    if (onVote) {
      onVote(optionId);
    }
  };

  const handleLike = () => {
    if (onLike) {
      onLike();
    }
  };

  const handleMenuPress = () => {
    setShowMenu(!showMenu);
  };

  const handleEdit = () => {
    setShowMenu(false);
    if (onEdit) onEdit();
  };

  const handleDelete = () => {
    setShowMenu(false);
    if (onDelete) onDelete();
  };

  const handleShare = async () => {
    try {
      console.log('🟣 [PollPost] Starting share...');
      console.log('🟣 Post ID:', post.id);
      console.log('🟣 Platform:', Platform.OS);
      
      const postUrl = `https://lnbapp.ln.ink/post/${post.id}`;
      const shareMessage = `Check out this poll on Leads n Business 📊\n\n${post.caption || ''}\n\n${postUrl}`;
      
      // Use default post.png image for polls
      let imageSource: string | undefined;
      let useDefaultImage = false;
      
      if (Platform.OS !== 'web') {
        console.log('🟣 Loading default post.png for poll');
        useDefaultImage = true;
        
        try {
          const asset = Asset.fromModule(require('../../../assets/post.png'));
          await asset.downloadAsync();
          imageSource = asset.localUri || asset.uri;
          console.log('🟣 Default image URI:', imageSource);
        } catch (assetError) {
          console.error('❌ Error loading default image:', assetError);
          imageSource = undefined;
        }
      }
      
      // If we have an image source (default image), share it
      if (imageSource && Platform.OS !== 'web') {
        console.log('🟣 Attempting to prepare image for sharing...');
        
        try {
          // Copy the asset to a file location that can be shared
          const fileUri = `${FileSystem.cacheDirectory}default_post.png`;
          
          await FileSystem.copyAsync({
            from: imageSource,
            to: fileUri,
          });
          
          console.log('🟣 Default image copied to:', fileUri);
          
          // Share image with message using react-native-share
          await Share.open({
            title: 'Share Poll',
            message: shareMessage,
            url: Platform.OS === 'android' ? `file://${fileUri}` : fileUri,
            type: 'image/png',
          });
          console.log('✅ Poll shared successfully with image');
          return;
        } catch (imageError: any) {
          if (imageError.message !== 'User did not share') {
            console.error('❌ Error preparing/sharing poll image:', imageError);
          }
        }
      } else {
        console.log('⚠️ Skipping image for web platform');
      }
      
      // Fallback: Share just the message with URL
      console.log('🟣 Falling back to message-only share');
      await Share.open({
        message: shareMessage,
      });
      console.log('✅ Share completed (message only)');
    } catch (error: any) {
      if (error.message !== 'User did not share') {
        console.error('Error sharing poll:', error);
      }
    }
  };

  const isOwner = currentUserId && currentUserId === post.userId;
  const canModify = isAdmin || isOwner;

  const getPercentage = (votes: number) => {
    if (post.totalVotes === 0) return 0;
    return Math.round((votes / post.totalVotes) * 100);
  };

  return (
    <View style={[styles.container, style]}>
      <View style={styles.postHeader}>
        <Pressable 
          style={styles.profileMeta}
          onPress={() => {
            console.log('[PollPost] User pressed, userId:', post.userId);
            onUserPress?.(post.userId);
          }}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          {post.avatar ? (
            <Image source={{ uri: post.avatar }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, { backgroundColor: theme.backgroundSecondary, justifyContent: 'center', alignItems: 'center' }]}>
              <Ionicons name="person" size={24} color={theme.textSecondary} />
            </View>
          )}
          <View style={styles.profileText}>
            <ThemedText variant="semibold" style={styles.username}>
              {post.user}
            </ThemedText>
            <ThemedText style={[styles.metaText, { color: theme.textSecondary }]}>
              {post.location}{post.createdAt ? ` • ${getTimeAgo(post.createdAt)}` : ''}
            </ThemedText>
          </View>
        </Pressable>
        <Pressable onPress={handleMenuPress}>
          <Ionicons name="ellipsis-horizontal" size={22} color={theme.textSecondary} />
        </Pressable>
      </View>

      {/* Menu Dropdown */}
      {showMenu && canModify && (
        <View style={[styles.menuContainer, { backgroundColor: theme.card, borderColor: theme.border }]}>
          {isOwner && (
            <Pressable onPress={handleEdit} style={styles.menuItem}>
              <Ionicons name="create-outline" size={20} color={theme.text} />
              <ThemedText style={styles.menuText}>Edit</ThemedText>
            </Pressable>
          )}
          <Pressable onPress={handleDelete} style={styles.menuItem}>
            <Ionicons name="trash-outline" size={20} color={theme.error} />
            <ThemedText style={[styles.menuText, { color: theme.error }]}>Delete</ThemedText>
          </Pressable>
        </View>
      )}

      {post.caption && (
        <>
          <ThemedText
            style={styles.captionText}
            numberOfLines={showFullCaption ? undefined : 3}
          >
            {post.caption}
          </ThemedText>
          {!showFullCaption && post.caption.length > 100 && (
            <Pressable onPress={handleExpandCaption} style={styles.seeMoreButton}>
              <ThemedText variant="semibold" style={[styles.seeMoreText, { color: theme.info }]}>
                See more
              </ThemedText>
            </Pressable>
          )}
        </>
      )}

      <View style={styles.pollContainer}>
        {(() => {
          const hasVoted = !!post.userVote;
          
          return post.pollOptions.map((option) => {
            const percentage = getPercentage(option.votes);
            const isSelected = post.userVote === option.id;
            
            return (
              <Pressable
                key={option.id}
                onPress={() => !hasVoted && handleVote(option.id)}
                disabled={hasVoted}
                style={({ pressed }) => [
                  styles.pollOption,
                  {
                    backgroundColor: theme.backgroundSecondary,
                    borderColor: isSelected ? theme.primary : theme.border,
                    borderWidth: isSelected ? 2 : 1,
                    opacity: pressed && !hasVoted ? 0.7 : 1,
                  },
                ]}
              >
                <View style={styles.pollOptionHeader}>
                  {!hasVoted && (
                    <View style={[
                      styles.radioButton,
                      { borderColor: theme.border }
                    ]}>
                    </View>
                  )}
                  <ThemedText variant="medium" style={styles.pollOptionText}>
                    {option.text}
                  </ThemedText>
                  {hasVoted && (
                    <ThemedText
                      variant="semibold"
                      style={[styles.pollPercentage, { color: theme.text }]}
                    >
                      {percentage}%
                    </ThemedText>
                  )}
                </View>
                {hasVoted && (
                  <View style={styles.pollProgressContainer}>
                    <View
                      style={[
                        styles.pollProgress,
                        {
                          width: `${percentage}%`,
                          backgroundColor: isSelected ? theme.primary : theme.border,
                        },
                      ]}
                    />
                  </View>
                )}
              </Pressable>
            );
          });
        })()}
        <ThemedText style={[styles.totalVotes, { color: theme.textSecondary }]}>
          {post.userVote ? `${post.totalVotes.toLocaleString()} votes` : 'Tap an option to vote'}
        </ThemedText>
      </View>

      <View style={styles.actionsRow}>
        {engagementActions.map((action) => (
          <Pressable 
            key={action.key} 
            style={styles.actionItem}
            onPress={action.key === 'likes' ? handleLike : action.key === 'comments' ? onCommentPress : undefined}
          >
            {action.key === 'likes' ? (
              <Ionicons 
                name={post.isLiked ? 'trending-up' : 'trending-up-outline'}
                size={24} 
                color={post.isLiked ? theme.primary : theme.text} 
              />
            ) : (
              <Ionicons 
                name={action.icon} 
                size={24} 
                color={theme.text} 
              />
            )}
            <ThemedText style={[styles.actionText, { color: theme.text }]}>
              {post[action.key]}
            </ThemedText>
          </Pressable>
        ))}
        <View style={styles.actionsSpacer} />
        <Pressable onPress={handleShare} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Ionicons name="paper-plane-outline" size={24} color={theme.text} />
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  profileMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  profileText: {
    marginLeft: 12,
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
  },
  username: {
    fontSize: 17,
  },
  metaText: {
    fontSize: 13,
    marginTop: 2,
  },
  captionText: {
    fontSize: 16,
    marginTop: 16,
  },
  seeMoreButton: {
    marginTop: 6,
    alignSelf: 'flex-start',
  },
  seeMoreText: {
    fontSize: 14,
  },
  pollContainer: {
    marginTop: 20,
  },
  pollOption: {
    borderRadius: 12,
    marginBottom: 12,
    padding: 14,
  },
  pollOptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  pollOptionText: {
    fontSize: 15,
    flex: 1,
  },
  pollPercentage: {
    fontSize: 15,
    marginLeft: 10,
  },
  pollProgressContainer: {
    height: 5,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 2.5,
    overflow: 'hidden',
  },
  pollProgress: {
    height: '100%',
    borderRadius: 2.5,
  },
  totalVotes: {
    fontSize: 12,
    marginTop: 4,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 18,
  },
  actionText: {
    fontSize: 15,
    marginLeft: 6,
  },
  actionsSpacer: {
    flex: 1,
  },
  menuContainer: {
    position: 'absolute',
    top: 50,
    right: 16,
    borderRadius: 8,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 1000,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  menuText: {
    fontSize: 15,
  },
  menuDivider: {
    height: 1,
  },
});

export default PollPost;
